import React, { useEffect, useRef, useState } from "react";
import ClassificationResultReport from "./ClassificationResultReport";
import GoogleSearchActions, { GoogleImageCheckCard, getImageCheckEnabled } from "./GoogleSearchActions";
import ProductIdentityCard from "./ProductIdentityCard";

const GATE_CACHE_PREFIX = "product-identity-gate:v5-image-check:";
const GATE_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const GATE_NEGATIVE_CACHE_TTL_MS = 5 * 60 * 1000;
const identityRequests = new Map();
const classificationRequests = new Map();

function gateCacheKey(query) {
  return `${GATE_CACHE_PREFIX}${String(query || "").replace(/\s+/g, " ").trim().toLowerCase()}`;
}

export function readGateState(query) {
  try {
    const raw = sessionStorage.getItem(gateCacheKey(query));
    if (!raw) return null;
    const saved = JSON.parse(raw);
    const ttl = saved?.state?.phase === "pass"
      ? GATE_NEGATIVE_CACHE_TTL_MS : GATE_CACHE_TTL_MS;
    if (!saved?.state || !saved.savedAt || Date.now() - saved.savedAt > ttl) {
      sessionStorage.removeItem(gateCacheKey(query));
      return null;
    }
    return saved.state;
  } catch (_) {
    return null;
  }
}

/** 조사 상태 공유 이벤트 — 사전 점검 카드와 AI 웹검증 블록이 같은 조사를 함께 본다. */
export const RESEARCH_STARTED_EVENT = "loginhs:product-research-started";
export const RESEARCH_UPDATED_EVENT = "loginhs:product-research-updated";
export function researchEventKey(query) {
  return gateCacheKey(query);
}
function emitResearchEvent(type, query) {
  try {
    window.dispatchEvent(new CustomEvent(type, { detail: { key: gateCacheKey(query), query } }));
  } catch (_) { /* 오래된 브라우저·테스트 환경 */ }
}
export function notifyProductResearchStarted(query) {
  emitResearchEvent(RESEARCH_STARTED_EVENT, query);
}

export function writeGateState(query, state) {
  try {
    sessionStorage.setItem(gateCacheKey(query), JSON.stringify({ savedAt: Date.now(), state }));
  } catch (_) { /* private mode/quota: in-flight deduplication still works */ }
  emitResearchEvent(RESEARCH_UPDATED_EVENT, query);
  return state;
}

function parseIdentityResponse(query, data) {
  const research = data?.product_research;
  const hasResearch = research && research.status !== "UNAVAILABLE"
    && (research.identity || research.summary || research.sources?.length);
  return data?.product_confirmation_required || hasResearch
    ? { query, phase: "confirm", research, result: null }
    : { query, phase: "pass", research: null, result: null };
}

/**
 * 사전 점검 카드가 "웹검증 결과로 사실 채우기"에 쓰는 상품 조사 결과. 캐시가 있으면 그대로,
 * 없으면 같은 상품 조사 요청을 한 번 보내 게이트와 같은 캐시에 저장한다(중복 요청 없음).
 */
export async function loadProductResearch(query) {
  const saved = readGateState(query);
  if (saved && saved.phase !== "idle") return saved.research || null;
  notifyProductResearchStarted(query);
  const state = await loadIdentityOnce(query);
  return state?.research || null;
}

function loadIdentityOnce(query) {
  const key = gateCacheKey(query);
  if (identityRequests.has(key)) return identityRequests.get(key);
  const request = fetch("/api/onto/product-research", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ q: query }),
  })
    .then((response) => (response.ok
      ? response.json()
      : Promise.reject(new Error(`HTTP ${response.status}`))))
    .then((data) => writeGateState(query, parseIdentityResponse(query, data)))
    .finally(() => identityRequests.delete(key));
  identityRequests.set(key, request);
  return request;
}

function loadClassificationOnce(query, confirmed, research) {
  const key = gateCacheKey(query);
  if (classificationRequests.has(key)) return classificationRequests.get(key);
  const request = fetch("/api/onto/ai-mode", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ q: query, confirmed_product_description: confirmed }),
  })
    .then((response) => (response.ok
      ? response.json()
      : Promise.reject(new Error(`HTTP ${response.status}`))))
    .then((data) => {
      const next = data?.product_confirmation_required
        ? { query, phase: "confirm", research: data.product_research, result: null }
        : {
          query,
          phase: "result",
          research: data?.product_research || research,
          result: normalizeAiModeResult(data, confirmed),
        };
      return writeGateState(query, next);
    })
    .finally(() => classificationRequests.delete(key));
  classificationRequests.set(key, request);
  return request;
}

function loadCandidateClassificationOnce(query, candidate, fact, research) {
  const key = gateCacheKey(query);
  if (classificationRequests.has(key)) return classificationRequests.get(key);
  const request = fetch("/api/onto/ai-mode", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      q: `${query}\n확인된 상품 사실: ${fact}`,
      selected_candidate_hsk: candidate,
    }),
  })
    .then((response) => (response.ok
      ? response.json()
      : Promise.reject(new Error(`HTTP ${response.status}`))))
    .then((data) => writeGateState(query, {
      query,
      phase: "result",
      research: data?.product_research || research,
      result: {
        ...normalizeAiModeResult(data),
        productResearch: data?.product_research || research,
        selectedFact: fact,
        selectedCandidateHsk: candidate,
      },
    }))
    .finally(() => classificationRequests.delete(key));
  classificationRequests.set(key, request);
  return request;
}

export function clearProductIdentityGateCache() {
  identityRequests.clear();
  classificationRequests.clear();
  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(GATE_CACHE_PREFIX))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch (_) { /* test/private mode */ }
}

export function normalizeAiModeResult(data, confirmedProductDescription = "") {
  return {
    content: data?.answer || "",
    citations: data?.citations,
    reverseValidation: data?.reverse_validation,
    classifierReverseValidation: data?.classifier_reverse_validation,
    productResearch: data?.product_research,
    webSources: data?.web_sources,
    confirmedProductDescription:
      confirmedProductDescription || data?.confirmed_product_description,
    inputAmbiguity: data?.input_ambiguity,
    candidateOptions: data?.candidate_options,
  };
}

function ProductResearchProgress({ query }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [query]);

  const stages = [
    "내부 상품 프로필 확인",
    "제조사·공식 출처 검색",
    "출처와 상품 설명 확인",
    "검색 결과 정리",
  ];
  const activeStage = elapsed < 3 ? 0 : elapsed < 12 ? 1 : elapsed < 24 ? 2 : 3;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        border: "1px solid #bfdbfe",
        borderRadius: 9,
        background: "#eff6ff",
        padding: "17px 20px 16px",
        margin: "10px 0 16px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
        <span className="audit-spinner" aria-hidden="true" style={{ width: 17, height: 17, flex: "0 0 auto" }} />
        <strong style={{ color: "#1e3a5f", fontSize: 14 }}>상품 정보를 검색하고 있습니다</strong>
        <span style={{ marginLeft: "auto", color: "#1d4ed8", fontSize: 12, fontVariantNumeric: "tabular-nums" }}>
          {elapsed}초 경과
        </span>
      </div>
      <div style={{ color: "#64748b", fontSize: 12, marginTop: 7, lineHeight: 1.5 }}>
        <strong style={{ color: "#475569" }}>&ldquo;{query}&rdquo;</strong>의 실제 상품과 신뢰할 수 있는 출처를 대조합니다. 보통 10~30초, 자료가 적은 품목은 1분까지 걸립니다.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 6, marginTop: 14 }}>
        {stages.map((stage, index) => {
          const done = index < activeStage;
          const active = index === activeStage;
          return (
            <div key={stage} style={{ minWidth: 0 }}>
              <div style={{ height: 4, borderRadius: 3, background: done || active ? "#3b82f6" : "#dbeafe", opacity: active ? 1 : done ? 0.75 : 1 }} />
              <div style={{ marginTop: 6, color: active ? "#1d4ed8" : done ? "#64748b" : "#94a3b8", fontSize: 10.5, fontWeight: active ? 700 : 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {done ? "✓ " : active ? "● " : "○ "}{stage}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ProductResearchResultProcess({ onRestart }) {
  const stages = ["상품 식별", "출처 확인", "참고 후보·역검증", "PROVE 사실 전달"];
  return (
    <div
      aria-label="AI 웹검증 진행 과정"
      style={{ border: "1px solid #bfdbfe", borderRadius: 9, background: "#eff6ff", padding: "12px 14px", margin: "8px 0 12px" }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <strong style={{ color: "#1e3a5f", fontSize: 13.5 }}>AI 웹검증 완료</strong>
        <span style={{ color: "#475569", fontSize: 11.5 }}>확인된 상품 사실만 PROVE가 다시 계산합니다.</span>
        <button
          type="button"
          onClick={onRestart}
          style={{ marginLeft: "auto", border: "1px solid #2563eb", borderRadius: 6, background: "#fff", color: "#1d4ed8", padding: "6px 10px", fontWeight: 700, cursor: "pointer" }}
        >
          AI 웹검증 다시 실행
        </button>
      </div>
      <ol style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 6, margin: "10px 0 0", padding: 0, listStyle: "none" }}>
        {stages.map((stage, index) => (
          <li key={stage} style={{ color: index < 3 ? "#18713a" : "#245a96", fontSize: 10.5, fontWeight: 700 }}>
            <span style={{ display: "inline-flex", width: 19, height: 19, alignItems: "center", justifyContent: "center", marginRight: 5, borderRadius: "50%", border: "1px solid #93c5fd", background: index < 3 ? "#dcfce7" : "#dbeafe" }}>
              {index < 3 ? "✓" : "4"}
            </span>
            {stage}
          </li>
        ))}
      </ol>
    </div>
  );
}

/**
 * 관세율표에 직접 매칭이 없을 때 키워드 부분일치 결과를 먼저 보여주지 않고,
 * AI 모드의 웹 상품 식별 단계를 재사용해 상품 확인 후 분류를 다시 실행한다.
 * 식별이 필요 없는 검색이나 API 오류는 기존 검색결과로 안전하게 통과한다.
 */
export default function ProductIdentityGate({ q, enabled, manual = false, onRowClick, onOpenProve, children }) {
  const [state, setState] = useState({ query: "", phase: "idle", research: null, result: null });
  const queryRef = useRef(q);
  const mountedRef = useRef(true);
  queryRef.current = q;

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if ((!enabled && !manual) || !q) {
      setState({ query: q || "", phase: "idle", research: null, result: null });
      return undefined;
    }

    let active = true;
    const pendingClassification = classificationRequests.get(gateCacheKey(q));
    if (pendingClassification) {
      const saved = readGateState(q);
      setState({ query: q, phase: "classifying", research: saved?.research, result: null });
      pendingClassification
        .then((next) => { if (active && queryRef.current === q) setState(next); })
        .catch(() => { if (active && queryRef.current === q) setState(saved || parseIdentityResponse(q, {})); });
      return () => { active = false; };
    }

    const saved = readGateState(q);
    if (saved) {
      setState(saved);
      return () => { active = false; };
    }

    if (manual) {
      setState({ query: q, phase: "manual", research: null, result: null });
      return () => { active = false; };
    }

    setState({ query: q, phase: "checking", research: null, result: null });
    loadIdentityOnce(q)
      .then((next) => { if (active && queryRef.current === q) setState(next); })
      .catch(() => {
        if (active && queryRef.current === q) {
          setState({ query: q, phase: "pass", research: null, result: null });
        }
      });

    return () => { active = false; };
  }, [enabled, manual, q]);

  // 사전 점검 카드가 같은 검색어를 조사하면 이 블록도 '조사 중'으로, 끝나면 그 결과(확인 카드)로 바뀐다.
  // 반대로 이 블록의 버튼으로 조사해도 카드가 같은 캐시를 읽는다. 조사는 어느 쪽이든 한 번만 돈다.
  useEffect(() => {
    if ((!enabled && !manual) || !q) return undefined;
    const key = gateCacheKey(q);
    const onStarted = (event) => {
      if (event?.detail?.key !== key) return;
      setState((previous) => (["idle", "manual", "pass"].includes(previous.phase)
        ? { query: q, phase: "checking", research: null, result: null }
        : previous));
    };
    const onUpdated = (event) => {
      if (event?.detail?.key !== key) return;
      const saved = readGateState(q);
      if (!saved) return;
      setState((previous) => (previous.phase === "classifying" || previous.phase === "candidate-classifying"
        ? previous
        : saved));
    };
    window.addEventListener(RESEARCH_STARTED_EVENT, onStarted);
    window.addEventListener(RESEARCH_UPDATED_EVENT, onUpdated);
    return () => {
      window.removeEventListener(RESEARCH_STARTED_EVENT, onStarted);
      window.removeEventListener(RESEARCH_UPDATED_EVENT, onUpdated);
    };
  }, [enabled, manual, q]);

  const startManualResearch = () => {
    if (!q || state.phase === "checking") return;
    const currentQuery = q;
    setState({ query: currentQuery, phase: "checking", research: null, result: null });
    loadIdentityOnce(currentQuery)
      .then((next) => {
        if (mountedRef.current && currentQuery === queryRef.current) setState(next);
      })
      .catch(() => {
        if (mountedRef.current && currentQuery === queryRef.current) {
          setState({ query: currentQuery, phase: "manual", research: null, result: null });
        }
      });
  };

  const restartResearch = () => {
    if (!q || state.phase === "checking") return;
    const currentQuery = q;
    const key = gateCacheKey(currentQuery);
    identityRequests.delete(key);
    classificationRequests.delete(key);
    try { sessionStorage.removeItem(key); } catch (_) { /* private mode */ }
    setState({ query: currentQuery, phase: "checking", research: null, result: null });
    loadIdentityOnce(currentQuery)
      .then((next) => {
        if (mountedRef.current && currentQuery === queryRef.current) setState(next);
      })
      .catch(() => {
        if (mountedRef.current && currentQuery === queryRef.current) {
          setState({ query: currentQuery, phase: "manual", research: null, result: null });
        }
      });
  };

  const confirmProduct = (description) => {
    const confirmed = String(description || "").trim();
    if (!confirmed || state.phase === "classifying") return;

    const currentQuery = q;
    setState((previous) => ({ ...previous, phase: "classifying" }));
    loadClassificationOnce(currentQuery, confirmed, state.research)
      .then((next) => {
        if (mountedRef.current && currentQuery === queryRef.current) setState(next);
      })
      .catch(() => {
        if (mountedRef.current && currentQuery === queryRef.current) {
          setState((previous) => ({ ...previous, phase: "confirm" }));
        }
      });
  };

  const selectCandidate = ({ hsk, fact }) => {
    const candidate = String(hsk || "").replace(/\D/g, "");
    const confirmedFact = String(fact || "").trim();
    if (candidate.length < 4 || !confirmedFact || state.phase === "candidate-classifying") return;
    const currentQuery = q;
    const previous = state;
    setState((current) => ({ ...current, phase: "candidate-classifying" }));
    loadCandidateClassificationOnce(currentQuery, candidate, confirmedFact, state.research)
      .then((next) => {
        if (mountedRef.current && currentQuery === queryRef.current) setState(next);
      })
      .catch(() => {
        if (mountedRef.current && currentQuery === queryRef.current) setState(previous);
      });
  };

  // 검색바의 「이미지 확인」 토글을 그대로 따른다 — 켜져 있으면 무료 외형 확인 카드가
  // 대신 뜨고, 흩어져 있던 구글 버튼 묶음은 숨긴다.
  const imageCheck = getImageCheckEnabled();

  const goHsk = (code) => {
    const digits = String(code || "").replace(/\D/g, "");
    if (digits.length >= 4) {
      onRowClick?.(digits.slice(0, 4), digits.length >= 10 ? digits.slice(0, 10) : null);
    }
  };

  // 「이미지 확인」이 켜져 있으면 상태와 무관하게 외형 확인 카드를 낸다.
  //
  // 이 카드는 게이트가 manual 단계일 때만 렌더되고 있었다. 내부 자료로 상품이 확인되는
  // 질의(pass)에서는 체크박스를 켜도 화면에 아무 변화가 없어, 검색바의 체크박스가 고장 난 것처럼
  // 보였다. 체크박스는 검색바에 있으므로 검색할 때마다 무언가 해야 한다.
  if ((!enabled && !manual) || state.phase === "idle" || state.phase === "pass") {
    return imageCheck && q ? (
      <>
        <div style={{ margin: "8px 0 12px" }}>
          <GoogleImageCheckCard query={q} />
        </div>
        {children}
      </>
    ) : children;
  }
  if (state.phase === "manual") {
    return (
      <>
        <div style={{ border: "1px solid #dbe3ec", borderRadius: 8, background: "#f8fafc", padding: "12px 14px", margin: "8px 0 12px", display: "grid", gap: 10 }}>
          <div aria-label="상품 상세 확인 작업" style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 12, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={startManualResearch}
              title="공개 웹의 제조사·공식 판정 자료를 대조합니다. 첫 조회는 20초 이상 걸릴 수 있습니다."
              style={{ order: 0, border: "1px solid #202124", borderRadius: 7, background: "#202124", color: "#fff", padding: "8px 13px", fontWeight: 750, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}
            >
              AI 웹검증
            </button>
            <div style={{ order: 1, flex: 1, minWidth: 260 }}>
              <strong style={{ color: "#334155", fontSize: 13 }}>AI 웹검증은 결정트리 사전 점검이 미확정이면 자동으로 실행됩니다.</strong>
              <div style={{ color: "#64748b", fontSize: 11.5, marginTop: 3 }}>
                지금 바로 실행하려면 왼쪽 버튼을 누르세요. 제조사·공식 자료로 재질·성분·용도를 확인하고, 이미지는 외형의 단서로만 씁니다.
              </div>
            </div>
            {!imageCheck && (
              <div style={{ order: 2 }}>
                <GoogleSearchActions
                  query={q}
                  compact
                  modes={["product", "image"]}
                  eventContext="product_identity_gate"
                />
              </div>
            )}
          </div>
          {imageCheck && <GoogleImageCheckCard query={q} />}
        </div>
        {children}
      </>
    );
  }
  if (state.phase === "checking") {
    return <ProductResearchProgress query={q} />;
  }
  if (state.phase === "candidate-classifying") {
    return (
      <div role="status" aria-live="polite" style={{ border: "1px solid #bfdbfe", borderRadius: 9, background: "#eff6ff", padding: "17px 20px", margin: "10px 0 16px", display: "flex", alignItems: "center", gap: 10 }}>
        <span className="audit-spinner" aria-hidden="true" style={{ width: 17, height: 17 }} />
        <div><strong style={{ color: "#1e3a5f", fontSize: 14 }}>선택한 필수 전제로 분류를 다시 검증하고 있습니다</strong><div style={{ color: "#64748b", fontSize: 11.5, marginTop: 3 }}>후보 생성과 역방향 검증을 다시 실행합니다.</div></div>
      </div>
    );
  }
  if (state.phase === "confirm" || state.phase === "classifying") {
    return (
      <ProductIdentityCard
        query={q}
        research={state.research}
        busy={state.phase === "classifying"}
        onConfirm={confirmProduct}
      />
    );
  }
  if (state.phase === "result" && state.result) {
    return (
      <div>
        <ProductResearchResultProcess onRestart={restartResearch} />
        <div style={{ color: "#475569", fontSize: 12.5, margin: "8px 2px" }}>
          AI 웹검증이 확인한 상품 설명으로 참고 후보와 역검증을 계산했습니다. PROVE 최종 판정은 아닙니다.
        </div>
        <ClassificationResultReport
          message={state.result}
          productName={q}
          onHsk={goHsk}
          onSelectCandidate={selectCandidate}
          advisoryMode="web"
          onOpenProve={() => onOpenProve?.({ reuseWebResearch: true })}
        />
      </div>
    );
  }
  return children;
}
